// F15a / FR-RD5: Settings -> Recording. Records a new global shortcut for start/stop,
// pause/resume and mark-moment, saves it through the desktop shell (which registers it and
// persists it to hotkeys.json), and shows "That shortcut is taken by another app" when the OS
// or another app owns it. Desktop only; the web app has no global hotkeys.
import { useEffect, useState, type KeyboardEvent } from "react";
import { Button, Kbd, formatHotkey } from "@grackle/ui";
import {
  acceleratorFromKeyEvent,
  acceleratorKeys,
  getHotkeys,
  isDesktop,
  isMobileShell,
  resetHotkeys,
  setHotkey,
  type HotkeyActionId,
  type HotkeysOut,
} from "../desktop";

const ACTIONS: { id: HotkeyActionId; label: string }[] = [
  { id: "start_stop", label: "Start / stop recording" },
  { id: "pause", label: "Pause / resume" },
  { id: "mark", label: "Mark a moment" },
];

function errorText(err: unknown): string {
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message;
  return "Couldn't save that shortcut.";
}

export default function HotkeySettings() {
  const [bindings, setBindings] = useState<HotkeysOut | null>(null);
  const [recording, setRecording] = useState<HotkeyActionId | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isDesktop() || isMobileShell()) return;
    getHotkeys()
      .then(setBindings)
      .catch(() => setBindings(null));
  }, []);

  if (!isDesktop() || isMobileShell() || !bindings) return null;

  async function onKeyDown(action: HotkeyActionId, e: KeyboardEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (e.key === "Escape") {
      setRecording(null);
      return;
    }
    const accelerator = acceleratorFromKeyEvent(e);
    if (!accelerator) return; // a lone modifier: keep listening
    setRecording(null);
    try {
      setBindings(await setHotkey(action, accelerator));
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function onReset() {
    try {
      setBindings(await resetHotkeys());
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <div>
      <div className="account-section-title">Recording</div>
      <div className="account-section-sub">Global shortcuts work while Grackle is in the background.</div>
      <ul className="hotkey-list" aria-label="Recording shortcuts">
        {ACTIONS.map(({ id, label }) => (
          <li key={id} className="hotkey-row">
            <span className="hotkey-row__label">{label}</span>
            <Kbd>{formatHotkey(acceleratorKeys(bindings[id]))}</Kbd>
            <Button
              size="sm"
              variant="secondary"
              aria-label={`Change ${label} shortcut`}
              onClick={() => setRecording(id)}
              onKeyDown={recording === id ? (e) => void onKeyDown(id, e) : undefined}
              onBlur={() => setRecording((cur) => (cur === id ? null : cur))}
            >
              {recording === id ? "Press keys…" : "Change"}
            </Button>
          </li>
        ))}
      </ul>
      {error && (
        <div role="alert" className="hotkey-error">
          {error}
        </div>
      )}
      <Button size="sm" variant="ghost" onClick={() => void onReset()}>
        Restore defaults
      </Button>
    </div>
  );
}
